import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Mobile-only bordered label/value list ("tabla-Card" in Figma) — the compact stand-in for
 * the desktop `Cell` grid (`data-grid.tsx`) on narrow screens, where a multi-column grid
 * wraps awkwardly. Wrap one or more `TablaCardRow`s in it.
 */
export const TablaCard = ({
    children,
    variant = 'outlined',
}: {
    children: ReactNode;
    /** `filled`: grey surface, no border — for lists nested inside a card (e.g. a claim's applications). */
    variant?: 'outlined' | 'filled';
}) => (
    <Stack
        spacing={1}
        sx={
            variant === 'filled'
                ? { bgcolor: 'grey.100', p: 1, width: '100%' }
                : { border: 1, borderColor: 'primary.mui.stroke', borderRadius: 2, p: 2, width: '100%' }
        }
    >
        {children}
    </Stack>
);

export const TablaCardRow = ({ label, value }: { label: string; value: ReactNode }) => (
    <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='body2' color='primary'>
            {label}
        </Typography>
        <Typography variant='body2' fontWeight='bold' color='primary'>
            {value}
        </Typography>
    </Stack>
);

/** Editable counterpart to `TablaCardRow`: label left, compact input right (no grey
 *  background). The input keeps a fixed comfortable width (CSS `fit-content` doesn't size
 *  these reliably — it either overlapped the label or over-truncated depending on the
 *  underlying field) while the label takes the rest of the row and wraps to a second line
 *  when it's too long. */
export const TablaCardEditRow = ({ label, children }: { label: string; children: ReactNode }) => (
    <Stack direction='row' alignItems='flex-start' spacing={2} sx={{ width: '100%' }}>
        <Typography variant='body2' color='primary' sx={{ pt: 1, flex: 1, minWidth: 0 }}>
            {label}
        </Typography>
        <Box sx={{ width: 150, flexShrink: 0 }}>{children}</Box>
    </Stack>
);
